import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  useDroppable,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  Typography,
  Card,
  Avatar,
  Chip,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Add,
  LocalShipping,
  LocationOn,
  CheckCircle,
  Schedule,
  LocalShipping as TransitIcon,
  Error as ErrorIcon,
  Inbox,
  DragIndicator,
  PersonOff,
} from "@mui/icons-material";
import {
  LoadingSpinner,
  EmptyState,
  SelectOrderDialog,
  PageHeader,
  FiltersCard,
} from "@/components/ui";
import {
  DeliveriesModeSwitch,
  DeliveriesFilters,
  useDeliveriesFilters,
} from "@/components/deliveries";
import {
  deliveryService,
  Delivery,
  DeliveryStatus,
} from "@/services/delivery.service";
import { driverService } from "@/services/driver.service";
import { Order } from "@/services/order.service";
import { formatCustomerName } from "@/utils/formatCustomerName";
import { useToastStore } from "@/store/toastStore";

const COLUMN_WIDTH = 320;
const UNASSIGNED_KEY = "unassigned";
const EMPTY_DELIVERIES: Delivery[] = [];
const DRIVER_COLUMN_PREFIX = "driver-";
const UNASSIGNED_COLUMN_ID = "unassigned-column";
const DROP_ANIMATION_DURATION = 220;

const SCROLLBAR = {
  "&::-webkit-scrollbar": { width: 6, height: 6 },
  "&::-webkit-scrollbar-track": { bgcolor: "action.hover" },
  "&::-webkit-scrollbar-thumb": { borderRadius: 3, bgcolor: "divider" },
};

function formatDriverName(firstName: string, lastName: string): string {
  const initial = lastName?.charAt(0)?.toUpperCase() ?? "";
  return `${firstName} ${initial}.`;
}

function parseHourFromTime(t: string): number {
  if (!t || !String(t).trim()) return 0;
  const s = String(t).trim();
  const iso = /^\d{4}-\d{2}-\d{2}T/.test(s) || s.endsWith("Z");
  const d = iso ? new Date(s) : new Date(`1970-01-01T${s}`);
  return d.getHours();
}

function isRushTime(t: string): boolean {
  const h = parseHourFromTime(t);
  return h >= 11 && h < 13;
}

function formatTimeDisplay(t: string | undefined | null, fallback = "-"): string {
  if (!t || !String(t).trim()) return fallback;
  const s = String(t).trim();
  if (/^\d{1,2}:\d{2}/.test(s)) return s.slice(0, 5);
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    const h = d.getHours().toString().padStart(2, "0");
    const m = d.getMinutes().toString().padStart(2, "0");
    return `${h}:${m}`;
  }
  return fallback;
}

/**
 * Extrait l'heure de livraison depuis une livraison
 */
function extractDeliveryTime(delivery: Delivery): string {
  return (
    delivery.scheduledDeliveryTime ||
    delivery.order?.deliveryTimeSlot?.split("-")[0]?.trim() ||
    ""
  );
}

/**
 * Trouve une livraison dans les listes groupées par livreur
 */
function isOrderCancelled(delivery: Delivery): boolean {
  return delivery.order?.status === "CANCELLED";
}

function findDeliveryInLists(
  deliveryId: string,
  lists: Record<string, Delivery[]>,
): { driverId: string; index: number; delivery: Delivery } | null {
  for (const [driverId, list] of Object.entries(lists)) {
    const index = list.findIndex((d) => d.id === deliveryId);
    if (index !== -1) {
      return { driverId, index, delivery: list[index] };
    }
  }
  return null;
}

type DeliveryCardStatusConfig = {
  label: string;
  color: "success" | "info" | "warning" | "error";
  Icon: typeof CheckCircle;
};

interface DeliveryCardContentProps {
  delivery: Delivery;
  showRush: boolean;
  time: string;
  getStatusConfig: (status: DeliveryStatus) => DeliveryCardStatusConfig;
  showDragHandle?: boolean;
  dragHandleProps?: { attributes: Record<string, unknown>; listeners: Record<string, unknown> };
  onClick?: () => void;
  isOverlay?: boolean;
  orderCancelled?: boolean;
}

interface SortableDeliveryItemProps {
  delivery: Delivery;
  showRush: boolean;
  time: string;
  navigate: (path: string) => void;
  getStatusConfig: (status: DeliveryStatus) => DeliveryCardStatusConfig;
}

interface DroppableColumnProps {
  id: string;
  children: React.ReactNode;
}

function DroppableColumn({ id, children }: DroppableColumnProps): JSX.Element {
  const { setNodeRef, isOver } = useDroppable({
    id,
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        flex: 1,
        minHeight: 200,
        position: "relative",
        ...(isOver && {
          bgcolor: "action.selected",
          border: "2px dashed",
          borderColor: "primary.main",
          borderRadius: 1,
        }),
      }}
    >
      {children}
    </Box>
  );
}

function DeliveryCardContent({
  delivery,
  showRush,
  time,
  getStatusConfig,
  showDragHandle = true,
  dragHandleProps,
  onClick,
  isOverlay = false,
  orderCancelled = false,
}: DeliveryCardContentProps): JSX.Element {
  const config = getStatusConfig(delivery.status);
  return (
    <Box>
      {showRush && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            py: 1,
          }}
        >
          <Box sx={{ flex: 1, height: "1px", bgcolor: "primary.main", opacity: 0.3 }} />
          <Chip
            icon={<Schedule sx={{ fontSize: 12 }} />}
            label="11h – 13h Rush"
            size="small"
            sx={{
              fontSize: "0.65rem",
              fontWeight: 700,
              textTransform: "uppercase",
              bgcolor: "action.hover",
              color: "primary.main",
              border: "1px solid",
              borderColor: "primary.light",
              "& .MuiChip-icon": { ml: 0.5 },
            }}
          />
          <Box sx={{ flex: 1, height: "1px", bgcolor: "primary.main", opacity: 0.3 }} />
        </Box>
      )}
      <Card
        variant="outlined"
        sx={{
          p: 1.5,
          borderRadius: 1.5,
          borderLeft: delivery.status === "PLANNED" ? 4 : 1,
          borderLeftColor:
            delivery.status === "PLANNED" ? "primary.main" : "divider",
          boxShadow: isOverlay ? 8 : 0,
          "&:hover": !isOverlay ? { boxShadow: 1 } : undefined,
          cursor: isOverlay ? "grabbing" : onClick ? "pointer" : "default",
          display: "flex",
          gap: 1,
          ...(isOverlay && {
            transform: "rotate(-1deg)",
            pointerEvents: "none",
          }),
        }}
        onClick={isOverlay ? undefined : onClick}
      >
        {showDragHandle && (
          <Box
            {...(dragHandleProps?.attributes ?? {})}
            {...(dragHandleProps?.listeners ?? {})}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: isOverlay ? "grabbing" : "grab",
              color: "text.secondary",
              "&:active": { cursor: "grabbing" },
              flexShrink: 0,
            }}
          >
            <DragIndicator sx={{ fontSize: 18 }} />
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 0.5,
            }}
          >
            {delivery.zone && (
              <Chip
                label={delivery.zone.name}
                size="small"
                sx={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  bgcolor: "action.hover",
                  color: "text.secondary",
                  height: 20,
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            )}
            {!delivery.zone && <Box />}
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {orderCancelled && (
                <Chip
                  label="Annulée"
                  size="small"
                  color="error"
                  sx={{ fontSize: "0.65rem", height: 20 }}
                />
              )}
              <Chip
                icon={
                  (() => {
                    const { Icon } = config;
                    return <Icon sx={{ fontSize: 10 }} />;
                  })()
                }
                label={config.label}
                size="small"
                color={config.color}
                sx={{
                  fontSize: "0.65rem",
                  height: 20,
                  "& .MuiChip-icon": { mr: 0.25 },
                }}
              />
            </Box>
          </Box>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, fontSize: "0.875rem", mb: 0.25 }}
          >
            {delivery.order?.customer ? formatCustomerName(delivery.order.customer) : "Client inconnu"}
          </Typography>
          {(delivery.order?.customerAddress?.address ?? delivery.order?.customer?.address) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                mb: 1,
              }}
            >
              <LocationOn sx={{ fontSize: 12 }} />
              <Typography variant="caption" color="text.secondary">
                {delivery.order?.customerAddress?.address ?? delivery.order?.customer?.address ?? "—"}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: 1,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.65rem",
                color: "text.secondary",
              }}
            >
              #{delivery.order?.orderNumber ?? "-"}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {delivery.estimatedTravelTimeMinutes != null && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                  ~{delivery.estimatedTravelTimeMinutes} min
                </Typography>
              )}
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, fontSize: "0.75rem" }}
              >
                {formatTimeDisplay(time || undefined, "-")}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}

function SortableDeliveryItem({
  delivery,
  showRush,
  time,
  navigate,
  getStatusConfig,
}: SortableDeliveryItemProps): JSX.Element {
  const cancelled = isOrderCancelled(delivery);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: delivery.id, disabled: cancelled });

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: isDragging ? undefined : CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.35 : cancelled ? 0.65 : 1,
      }}
    >
      <DeliveryCardContent
        delivery={delivery}
        showRush={showRush}
        time={time}
        getStatusConfig={getStatusConfig}
        showDragHandle={!cancelled}
        dragHandleProps={
          cancelled
            ? undefined
            : {
                attributes: attributes as unknown as Record<string, unknown>,
                listeners: (listeners ?? {}) as unknown as Record<string, unknown>,
              }
        }
        onClick={() => navigate(`/orders/${delivery.orderId}`)}
        orderCancelled={cancelled}
      />
    </Box>
  );
}

export function DeliveriesByDriverPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success, error: showError } = useToastStore();
  const { filters } = useDeliveriesFilters();

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignDriverId, setAssignDriverId] = useState<string | null>(null);
  const [activeDrag, setActiveDrag] = useState<{
    delivery: Delivery;
    showRush: boolean;
    time: string;
  } | null>(null);

  const { data: drivers = [], isLoading: driversLoading } = useQuery({
    queryKey: ["drivers"],
    queryFn: () => driverService.getAll(false),
  });

  const { data: deliveriesData, isLoading: deliveriesLoading } = useQuery({
    queryKey: [
      "deliveries",
      "by-driver",
      filters.status,
      filters.driver,
      filters.zone,
      filters.date,
    ],
    queryFn: () =>
      deliveryService.getAll({
        status: filters.status === "all" ? undefined : filters.status,
        driverId: filters.driver === "all" ? undefined : filters.driver,
        zoneId: filters.zone === "all" ? undefined : filters.zone,
        deliveryDate: filters.date || undefined,
      }),
  });
  const deliveries = deliveriesData ?? EMPTY_DELIVERIES;

  const assignMutation = useMutation({
    mutationFn: ({
      order,
      driverId,
    }: { order: Order; driverId: string }) => {
      const zoneId = order.customerAddress?.zone?.id ?? undefined;
      if (!zoneId) {
        return Promise.reject(new Error("Zone inconnue pour cette commande."));
      }
      return deliveryService.create({
        orderId: order.id,
        driverId,
        zoneId,
        deliveryDate: filters.date,
        scheduledDeliveryTime: order.deliveryTimeSlot?.split("-")[0]?.trim(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deliveries"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      success("Commande assignée au livreur.");
      setAssignOpen(false);
      setAssignDriverId(null);
    },
    onError: (err: any) => {
      showError(
        err?.response?.data?.message ?? "Erreur lors de l'assignation.",
      );
    },
  });

  const updateTourOrderMutation = useMutation({
    mutationFn: ({
      deliveryId,
      tourOrder,
    }: { deliveryId: string; tourOrder: number }) =>
      deliveryService.update(deliveryId, { tourOrder }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deliveries"] });
    },
    onError: (err: any) => {
      showError(
        err?.response?.data?.message ??
          "Erreur lors de la mise à jour de l'ordre.",
      );
    },
  });

  const reassignDeliveryMutation = useMutation({
    mutationFn: ({
      deliveryId,
      driverId,
      tourOrder,
    }: {
      deliveryId: string;
      driverId: string | null;
      tourOrder?: number;
    }) => {
      console.log("[reassignDeliveryMutation] deliveryId:", deliveryId, "driverId:", driverId, "tourOrder:", tourOrder);
      
      if (driverId === null) {
        // Désassigner la livraison
        console.log("[reassignDeliveryMutation] Unassigning delivery, calling update with driverId: null");
        return deliveryService.update(deliveryId, {
          driverId: null as any, // Backend accepte maintenant null pour désassigner
          tourOrder: undefined,
        });
      }
      console.log("[reassignDeliveryMutation] Assigning delivery, calling assign");
      return deliveryService.assign(deliveryId, {
        driverId,
        tourOrder,
      });
    },
    onSuccess: (data) => {
      console.log("[reassignDeliveryMutation] Success:", data);
      queryClient.invalidateQueries({ queryKey: ["deliveries"] });
      success("Livraison réassignée avec succès.");
    },
    onError: (err: any) => {
      console.error("[reassignDeliveryMutation] Error:", err);
      console.error("[reassignDeliveryMutation] Error response:", err?.response?.data);
      console.error("[reassignDeliveryMutation] Error status:", err?.response?.status);
      showError(
        err?.response?.data?.message ??
          "Erreur lors de la réassignation de la livraison.",
      );
    },
  });

  const reassignAbsentDriverMutation = useMutation({
    mutationFn: ({ driverId, deliveryDate }: { driverId: string; deliveryDate: string }) =>
      deliveryService.reassignAbsentDriver(driverId, deliveryDate),
    onSuccess: async (data) => {
      await queryClient.refetchQueries({ queryKey: ["deliveries"] });
      if (data.unassigned === 0) {
        success("Aucune livraison à réaffecter pour ce livreur à cette date.");
      } else {
        success(
          `Réaffectation : ${data.unassigned} livraison(s) répartie(s) (${data.reassigned} assignée(s)).`,
        );
      }
    },
    onError: (err: unknown) => {
      const message =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(message ?? "Erreur lors de la réaffectation (livreur absent).");
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const filteredBySearch = useMemo(() => {
    if (!filters.search) return deliveries;
    const q = filters.search.toLowerCase();
    return deliveries.filter(
      (d) =>
        d.order?.orderNumber?.toLowerCase().includes(q) ||
        (d.order?.customer && formatCustomerName(d.order.customer).toLowerCase().includes(q)) ||
        d.order?.customer?.firstName?.toLowerCase().includes(q) ||
        d.order?.customer?.lastName?.toLowerCase().includes(q) ||
        d.driver?.firstName?.toLowerCase().includes(q) ||
        d.driver?.lastName?.toLowerCase().includes(q) ||
        d.zone?.name?.toLowerCase().includes(q),
    );
  }, [deliveries, filters.search]);

  const [localDeliveriesByDriver, setLocalDeliveriesByDriver] = useState<
    Record<string, Delivery[]>
  >({});

  const deliveriesByDriver = useMemo(() => {
    const grouped: Record<string, Delivery[]> = {};
    filteredBySearch.forEach((d) => {
      const id = d.driverId || UNASSIGNED_KEY;
      if (!grouped[id]) grouped[id] = [];
      grouped[id].push(d);
    });
    grouped[UNASSIGNED_KEY] = [];
    filteredBySearch
      .filter(
        (d) =>
          !d.driverId || !drivers.some((dr) => dr.id === d.driverId),
      )
      .forEach((d) => grouped[UNASSIGNED_KEY].push(d));
    Object.keys(grouped).forEach((id) => {
      if (id === UNASSIGNED_KEY) return;
      grouped[id].sort((a, b) => {
        if (a.tourOrder !== b.tourOrder) return a.tourOrder - b.tourOrder;
        const ta =
          a.scheduledDeliveryTime ||
          a.order?.deliveryTimeSlot?.split("-")[0]?.trim() ||
          "00:00";
        const tb =
          b.scheduledDeliveryTime ||
          b.order?.deliveryTimeSlot?.split("-")[0]?.trim() ||
          "00:00";
        return ta.localeCompare(tb);
      });
    });
    return grouped;
  }, [filteredBySearch, drivers]);

  // Clé stable incluant les affectations (id + driverId) pour resynchroniser après "réassigner livreur absent".
  const syncKey = useMemo(
    () =>
      filteredBySearch
        .map((d) => `${d.id}:${d.driverId ?? "null"}`)
        .sort()
        .join(","),
    [filteredBySearch],
  );
  const deliveriesByDriverRef = useRef(deliveriesByDriver);
  deliveriesByDriverRef.current = deliveriesByDriver;

  // Resynchroniser l'état local quand les affectations changent (syncKey = id:driverId).
  useEffect(() => {
    setLocalDeliveriesByDriver(deliveriesByDriverRef.current);
  }, [syncKey]);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const deliveryId = String(event.active.id);
      const found = findDeliveryInLists(deliveryId, localDeliveriesByDriver);
      if (!found) return;

      const { delivery, index, driverId } = found;
      const list = localDeliveriesByDriver[driverId] ?? [];
      const time = extractDeliveryTime(delivery);
      const prevTime = index > 0 ? extractDeliveryTime(list[index - 1]) : "";
      const showRush = isRushTime(time) && !isRushTime(prevTime);
      setActiveDrag({ delivery, showRush, time });
    },
    [localDeliveriesByDriver],
  );

  /**
   * Trouve la colonne source d'une livraison
   * @returns string | null | undefined - string pour driverId, null pour unassigned, undefined si non trouvé
   */
  const findSourceColumn = useCallback(
    (deliveryId: string): string | null | undefined => {
      const found = findDeliveryInLists(deliveryId, localDeliveriesByDriver);
      if (!found) return undefined;
      return found.driverId === UNASSIGNED_KEY ? null : found.driverId;
    },
    [localDeliveriesByDriver],
  );

  /**
   * Trouve la colonne cible depuis l'ID de drop
   */
  const findTargetColumn = useCallback(
    (
      overId: string,
    ): { driverId: string | null; index?: number } | null => {
      console.log("[findTargetColumn] overId:", overId);
      
      // Vérifier d'abord si c'est une colonne droppable
      if (overId.startsWith(DRIVER_COLUMN_PREFIX)) {
        const driverId = overId.replace(DRIVER_COLUMN_PREFIX, "");
        console.log("[findTargetColumn] Dropped on driver column:", driverId);
        return { driverId };
      }
      if (overId === UNASSIGNED_COLUMN_ID) {
        console.log("[findTargetColumn] Dropped on unassigned column");
        return { driverId: null };
      }
      
      // Drop sur une autre livraison - trouver sa colonne
      const found = findDeliveryInLists(overId, localDeliveriesByDriver);
      if (found) {
        const driverId = found.driverId === UNASSIGNED_KEY ? null : found.driverId;
        console.log("[findTargetColumn] Dropped on delivery in column:", driverId, "at index:", found.index);
        return {
          driverId,
          index: found.index,
        };
      }
      
      console.warn("[findTargetColumn] No target found for overId:", overId);
      return null;
    },
    [localDeliveriesByDriver],
  );

  /**
   * Réorganise les livraisons dans la même colonne
   */
  const handleReorderInSameColumn = useCallback(
    (
      deliveryId: string,
      driverId: string | null,
      targetIndex: number | undefined,
      overId: string,
    ) => {
      const sourceKey = driverId || UNASSIGNED_KEY;
      const list = localDeliveriesByDriver[sourceKey] ?? [];
      const oldIndex = list.findIndex((d) => d.id === deliveryId);
      const newIndex =
        targetIndex !== undefined
          ? targetIndex
          : list.findIndex((d) => d.id === overId);

      if (oldIndex === -1 || newIndex === -1) return;

      const newList = arrayMove(list, oldIndex, newIndex);
      setLocalDeliveriesByDriver((prev) => ({
        ...prev,
        [sourceKey]: newList,
      }));

      // Mettre à jour le tourOrder pour chaque livraison affectée (seulement si assignée)
      if (driverId !== null) {
        newList.forEach((delivery, idx) => {
          const newTourOrder = idx + 1;
          if (delivery.tourOrder !== newTourOrder) {
            updateTourOrderMutation.mutate({
              deliveryId: delivery.id,
              tourOrder: newTourOrder,
            });
          }
        });
      }
    },
    [localDeliveriesByDriver, updateTourOrderMutation],
  );

  /**
   * Réassigner une livraison vers une autre colonne
   */
  const handleReassignToOtherColumn = useCallback(
    (
      deliveryId: string,
      sourceDriverId: string | null,
      targetDriverId: string | null,
      targetIndex: number | undefined,
    ) => {
      console.log("[handleReassignToOtherColumn] deliveryId:", deliveryId, "sourceDriverId:", sourceDriverId, "targetDriverId:", targetDriverId);
      
      const sourceKey = sourceDriverId || UNASSIGNED_KEY;
      const sourceList = localDeliveriesByDriver[sourceKey] ?? [];
      const delivery = sourceList.find((d) => d.id === deliveryId);
      if (!delivery) {
        console.warn("[handleReassignToOtherColumn] Delivery not found in source list");
        return;
      }

      if (targetDriverId !== null && isOrderCancelled(delivery)) {
        showError("Impossible d'assigner une commande annulée.");
        return;
      }

      console.log("[handleReassignToOtherColumn] Found delivery:", delivery.id);

      // Retirer de la colonne source
      const newSourceList = sourceList.filter((d) => d.id !== deliveryId);
      const newLocalDeliveries = {
        ...localDeliveriesByDriver,
        [sourceKey]: newSourceList,
      };

      // Ajouter à la colonne destination
      const targetKey = targetDriverId || UNASSIGNED_KEY;
      console.log("[handleReassignToOtherColumn] targetKey:", targetKey);
      const targetList = localDeliveriesByDriver[targetKey] ?? [];
      let newTargetList: Delivery[];
      if (targetIndex !== undefined) {
        newTargetList = [...targetList];
        newTargetList.splice(targetIndex, 0, delivery);
      } else {
        newTargetList = [...targetList, delivery];
      }

      console.log("[handleReassignToOtherColumn] newTargetList length:", newTargetList.length);

      newLocalDeliveries[targetKey] = newTargetList;
      setLocalDeliveriesByDriver(newLocalDeliveries);

      // Calculer le nouveau tourOrder (seulement si assigné à un livreur)
      const newTourOrder =
        targetDriverId !== null
          ? targetIndex !== undefined
            ? targetIndex + 1
            : newTargetList.length
          : undefined;

      console.log("[handleReassignToOtherColumn] Calling mutation with driverId:", targetDriverId, "tourOrder:", newTourOrder);

      // Réassigner via l'API
      reassignDeliveryMutation.mutate({
        deliveryId: delivery.id,
        driverId: targetDriverId,
        tourOrder: newTourOrder,
      });

      // Mettre à jour les tourOrder des autres livraisons dans la colonne source (seulement si assignée)
      if (sourceDriverId !== null) {
        newSourceList.forEach((d, idx) => {
          const newTourOrder = idx + 1;
          if (d.tourOrder !== newTourOrder) {
            updateTourOrderMutation.mutate({
              deliveryId: d.id,
              tourOrder: newTourOrder,
            });
          }
        });
      }

      // Mettre à jour les tourOrder des autres livraisons dans la colonne destination (seulement si assignée)
      if (targetDriverId !== null) {
        newTargetList.forEach((d, idx) => {
          const newTourOrder = idx + 1;
          if (d.tourOrder !== newTourOrder && d.id !== deliveryId) {
            updateTourOrderMutation.mutate({
              deliveryId: d.id,
              tourOrder: newTourOrder,
            });
          }
        });
      }
    },
    [
      localDeliveriesByDriver,
      reassignDeliveryMutation,
      updateTourOrderMutation,
      showError,
    ],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setTimeout(() => setActiveDrag(null), DROP_ANIMATION_DURATION);
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const deliveryId = String(active.id);
      const overId = String(over.id);

      console.log("[DragEnd] deliveryId:", deliveryId, "overId:", overId);

      const sourceDriverId = findSourceColumn(deliveryId);
      console.log("[DragEnd] sourceDriverId:", sourceDriverId);
      if (sourceDriverId === undefined) {
        console.warn("[DragEnd] Source column not found");
        return;
      }

      const target = findTargetColumn(overId);
      console.log("[DragEnd] target:", target);
      if (!target) {
        console.warn("[DragEnd] Target column not found for overId:", overId);
        return;
      }

      const { driverId: targetDriverId, index: targetIndex } = target;
      console.log("[DragEnd] targetDriverId:", targetDriverId, "targetIndex:", targetIndex);

      // Si c'est la même colonne, juste réorganiser
      // Comparaison stricte pour gérer null correctement
      if (sourceDriverId === targetDriverId || (sourceDriverId === null && targetDriverId === null)) {
        console.log("[DragEnd] Reordering in same column");
        handleReorderInSameColumn(
          deliveryId,
          sourceDriverId,
          targetIndex,
          overId,
        );
      } else {
        // Réassigner à une autre colonne
        console.log("[DragEnd] Reassigning to other column");
        handleReassignToOtherColumn(
          deliveryId,
          sourceDriverId,
          targetDriverId,
          targetIndex,
        );
      }
    },
    [findSourceColumn, findTargetColumn, handleReorderInSameColumn, handleReassignToOtherColumn],
  );

  const unassignedDeliveries = useMemo(
    () => localDeliveriesByDriver[UNASSIGNED_KEY] ?? [],
    [localDeliveriesByDriver],
  );

  const excludeOrderIds = useMemo(
    () => deliveries.map((d) => d.orderId).filter(Boolean),
    [deliveries],
  );

  const handleAssignClick = (driverId: string) => {
    setAssignDriverId(driverId);
    setAssignOpen(true);
  };

  const handleAssignSelect = (order: Order, driverId?: string) => {
    if (order.status === "CANCELLED") {
      showError("Impossible d'assigner une commande annulée.");
      return;
    }
    const id = driverId ?? assignDriverId;
    if (!id) return;
    assignMutation.mutate({ order, driverId: id });
  };

  const getStatusConfig = (
    status: DeliveryStatus,
  ): { label: string; color: "success" | "info" | "warning" | "error"; Icon: typeof CheckCircle } => {
    switch (status) {
      case "DELIVERED":
        return { label: "Livrée", color: "success", Icon: CheckCircle };
      case "IN_PROGRESS":
        return { label: "En cours", color: "info", Icon: TransitIcon };
      case "FAILED":
        return { label: "Échouée", color: "error", Icon: ErrorIcon };
      default:
        return { label: "Planifiée", color: "warning", Icon: Schedule };
    }
  };

  if (driversLoading || deliveriesLoading) {
    return (
      <LoadingSpinner message="Chargement des livraisons par livreur..." />
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Livraisons par Livreur"
        action={<DeliveriesModeSwitch />}
      />

      <FiltersCard>
        <DeliveriesFilters />
      </FiltersCard>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "nowrap",
            overflowX: "auto",
            gap: 3,
            pb: 1,
            alignItems: "stretch",
            ...SCROLLBAR,
          }}
        >
          {drivers.map((driver) => {
            const list = localDeliveriesByDriver[driver.id] ?? [];
            const deliveryIds = list.map((d) => d.id);
            return (
              <Box
                key={driver.id}
                sx={{
                  flexShrink: 0,
                  width: COLUMN_WIDTH,
                  minWidth: COLUMN_WIDTH,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Card
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: 1,
                  }}
                >
                  {/* Header maquette: avatar + nom + véhicule | count + print */}
                  <Box
                    sx={{
                      p: 2,
                      borderBottom: 1,
                      borderColor: "divider",
                      background:
                        "linear-gradient(to bottom, white, rgba(0,0,0,0.02))",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 1.5, minWidth: 0, flex: 1 }}>
                      <Avatar
                        sx={{
                          flexShrink: 0,
                          bgcolor: "primary.main",
                          width: 40,
                          height: 40,
                          fontSize: "0.9rem",
                        }}
                      >
                        {driver.firstName?.charAt(0)}
                        {driver.lastName?.charAt(0)}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700, fontSize: "0.875rem" }}
                          noWrap
                        >
                          {formatDriverName(driver.firstName, driver.lastName)}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.25,
                            color: "text.secondary",
                          }}
                        >
                          <LocalShipping sx={{ fontSize: 14 }} />
                          <Typography variant="caption">
                            Livraison
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {list.length > 0 && filters.date && (
                        <Tooltip title="Réaffecter les livraisons (livreur absent)">
                          <IconButton
                            size="small"
                            color="secondary"
                            disabled={reassignAbsentDriverMutation.isPending}
                            onClick={() =>
                              reassignAbsentDriverMutation.mutate({
                                driverId: driver.id,
                                deliveryDate: filters.date!,
                              })
                            }
                            aria-label="Réaffecter livreur absent"
                          >
                            <PersonOff fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Chip
                        label={list.length}
                        size="small"
                        sx={{
                          bgcolor: "action.hover",
                          color: "text.secondary",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Liste des livraisons — style maquette avec drag and drop */}
                  <DroppableColumn id={`${DRIVER_COLUMN_PREFIX}${driver.id}`}>
                    <Box
                      sx={{
                        flex: 1,
                        overflowY: "auto",
                        minHeight: 200,
                        maxHeight: "calc(100vh - 320px)",
                        p: 1.5,
                        bgcolor: "grey.50",
                        ...SCROLLBAR,
                      }}
                    >
                      {list.length === 0 ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            py: 4,
                            color: "text.secondary",
                          }}
                        >
                          <LocalShipping
                            sx={{ fontSize: 40, color: "action.disabled", mb: 1 }}
                          />
                          <Typography variant="body2">
                            Aucune livraison assignée
                          </Typography>
                        </Box>
                      ) : (
                        <SortableContext
                          items={deliveryIds}
                          strategy={verticalListSortingStrategy}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1.5,
                            }}
                          >
                            {list.map((delivery, idx) => {
                              const time = extractDeliveryTime(delivery);
                              const prevTime =
                                idx > 0 ? extractDeliveryTime(list[idx - 1]) : "";
                              const showRush =
                                isRushTime(time) && !isRushTime(prevTime);

                              return (
                                <SortableDeliveryItem
                                  key={delivery.id}
                                  delivery={delivery}
                                  showRush={showRush}
                                  time={time}
                                  navigate={navigate}
                                  getStatusConfig={getStatusConfig}
                                />
                              );
                            })}
                          </Box>
                        </SortableContext>
                      )}
                    </Box>
                  </DroppableColumn>

                {/* Assigner une commande — style maquette */}
                <Box
                  sx={{
                    p: 1.5,
                    borderTop: 1,
                    borderColor: "divider",
                    bgcolor: "grey.50",
                  }}
                >
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => handleAssignClick(driver.id)}
                    disabled={assignMutation.isPending}
                    sx={{
                      py: 1,
                      borderStyle: "dashed",
                      color: "text.secondary",
                      "&:hover": {
                        borderStyle: "dashed",
                        bgcolor: "background.paper",
                        borderColor: "primary.main",
                        color: "primary.main",
                      },
                    }}
                  >
                    Assigner une commande
                  </Button>
                </Box>
              </Card>
            </Box>
          );
        })}

        {/* Colonne Non assignées — style maquette */}
        <Box
          sx={{
            flexShrink: 0,
            width: COLUMN_WIDTH,
            minWidth: COLUMN_WIDTH,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Card
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: "grey.100",
              border: "1px dashed",
              borderColor: "divider",
              opacity: 0.9,
              "&:hover": { opacity: 1 },
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: 1,
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Inbox color="action" />
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, fontSize: "0.875rem" }}
                >
                  Non assignées
                </Typography>
              </Box>
              <Chip
                label={unassignedDeliveries.length}
                size="small"
                sx={{
                  bgcolor: "action.hover",
                  color: "text.secondary",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                }}
              />
            </Box>
            <DroppableColumn id={UNASSIGNED_COLUMN_ID}>
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  minHeight: 200,
                  maxHeight: "calc(100vh - 320px)",
                  p: 1.5,
                  ...SCROLLBAR,
                  position: "relative",
                }}
              >
                {unassignedDeliveries.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      py: 4,
                      color: "text.disabled",
                      textAlign: "center",
                      minHeight: "100%",
                    }}
                  >
                    <Typography variant="body2">
                      Aucune commande en attente
                    </Typography>
                  </Box>
                ) : (
                  <SortableContext
                    items={unassignedDeliveries.map((d) => d.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                    >
                      {unassignedDeliveries.map((delivery) => (
                        <SortableDeliveryItem
                          key={delivery.id}
                          delivery={delivery}
                          showRush={false}
                          time={extractDeliveryTime(delivery)}
                          navigate={navigate}
                          getStatusConfig={getStatusConfig}
                        />
                      ))}
                    </Box>
                  </SortableContext>
                )}
              </Box>
            </DroppableColumn>
          </Card>
        </Box>
        </Box>
        <DragOverlay
          dropAnimation={{
            duration: DROP_ANIMATION_DURATION - 20,
            easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
          }}
          style={{ cursor: "grabbing" }}
          zIndex={1400}
        >
          {activeDrag ? (
            <Box sx={{ width: COLUMN_WIDTH - 24 }}>
              <DeliveryCardContent
                delivery={activeDrag.delivery}
                showRush={activeDrag.showRush}
                time={activeDrag.time}
                getStatusConfig={getStatusConfig}
                showDragHandle
                isOverlay
              />
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>

      {drivers.length === 0 && unassignedDeliveries.length === 0 && (
        <EmptyState
          title="Aucun livreur"
          description="Créez des livreurs pour organiser les livraisons."
        />
      )}

      <SelectOrderDialog
        open={assignOpen}
        onClose={() => {
          setAssignOpen(false);
          setAssignDriverId(null);
        }}
        onSelect={handleAssignSelect}
        deliveryDate={filters.date}
        excludeOrderIds={excludeOrderIds}
        driverId={assignDriverId ?? undefined}
      />
    </Box>
  );
}
